import type { ChangeEvent, FC, FocusEvent, ReactElement } from 'react'
import { useEffect, useState } from 'react'

import type { WhiteLabel } from '@skytech/types'

import { ErrorMessage } from '../ErrorMessage'
import { Label } from '../Label'

import './styles.scss'

export const Checkbox: FC<{
  id: string
  checked: boolean
  error?: string | ReactElement
  touched?: boolean
  label?: string | ReactElement
  capitalizeLabel?: boolean
  marginBottom?: number
  marginTop?: number
  marginLeft?: number
  className?: string
  name?: string
  isLoading?: boolean
  isDisabled?: boolean
  onChange: (e: ChangeEvent<HTMLInputElement>) => void
  onBlur?: (e: FocusEvent<unknown, Element>) => void
  onFieldChange?: () => void
}> = ({
  id,
  label,
  checked,
  capitalizeLabel,
  marginBottom,
  marginTop,
  name,
  error,
  touched,
  className = '',
  isLoading,
  isDisabled,
  onBlur,
  onChange,
  onFieldChange,
}) => {
  const [focused, setFocused] = useState(false)

  useEffect(() => {
    isLoading && setFocused(false)
  }, [isLoading])

  const onFocus = () => {
    setFocused(true)
  }
  const onDefaultBlur = () => setFocused(false)
  const onCustomBlur = (e: FocusEvent<unknown, Element>) => {
    onBlur?.(e)
    setFocused(false)
  }

  // todo: replace with the org from url when ready
  const whiteLabel = localStorage.getItem('org') as WhiteLabel

  const classNameContainer = `ui-checkbox ${className} ${focused ? 'is-focused' : 'is-blurred'} ${
    checked ? 'is-checked' : ''
  } ${isDisabled ? 'is-disabled' : ''} ${whiteLabel}`
  const classNameCheckmark = `checkmark ${isLoading ? 'is-loading' : ''}`

  return (
    <label
      htmlFor={id}
      className={classNameContainer}
      style={{
        marginBottom: marginBottom || 0,
        marginTop: typeof marginTop === 'number' ? marginTop : '1rem',
      }}
    >
      {label && (
        <Label capitalizeLabel={capitalizeLabel} htmlFor={id} marginBottom={0}>
          {label}
        </Label>
      )}

      <input
        id={id}
        name={name}
        type="checkbox"
        checked={checked}
        disabled={isDisabled}
        onChange={e => {
          onChange(e)
          if (onFieldChange) onFieldChange()
        }}
        onFocus={onFocus}
        // Crucial for setting the "touched" fields
        onBlur={onCustomBlur || onDefaultBlur}
      />

      <span className={classNameCheckmark} />

      {error && touched && <ErrorMessage>{error}</ErrorMessage>}
    </label>
  )
}
